import { Image } from 'expo-image';
import { router } from 'expo-router';
import { CarFront, Share2 } from 'lucide-react-native';
import { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import type { GestureResponderEvent } from 'react-native';

import { Button } from '@/src/components/Button';
import { PressableScale } from '@/src/components/PressableScale';
import { useToast } from '@/src/components/Toast';
import { printGarageVehicleQrPlacard } from '@/src/features/garage/garage-qr-print';
import { GarageVehicleShareModal } from '@/src/features/garage/GarageVehicleShareModal';
import {
  garageVehiclePath,
  garageVehicleShareURL,
  garageVehicleTitle,
  type GarageVehicle,
} from '@/src/features/garage/garage-service';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

function ownershipLabel(value: GarageVehicle['ownershipStatus']) {
  if (value === 'past') {
    return 'Past';
  }

  if (value === 'project') {
    return 'Project';
  }

  return 'Current';
}

export function GarageCard({
  canEdit,
  vehicle,
}: {
  canEdit?: boolean;
  vehicle: GarageVehicle;
}) {
  const { showError, showSuccess } = useToast();
  const path = garageVehiclePath(vehicle);
  const shareURL = garageVehicleShareURL(vehicle);
  const imageURL =
    vehicle.coverPhoto?.thumbnail?.downloadURL ??
    vehicle.coverPhoto?.downloadURL ??
    null;
  const [printingQr, setPrintingQr] = useState(false);
  const [shareMenuVisible, setShareMenuVisible] = useState(false);
  const canShareOnFrienzzi = Boolean(canEdit);

  function openVehicle() {
    if (path) {
      router.push(path);
    }
  }

  function editVehicle(event: GestureResponderEvent) {
    event.stopPropagation();
    router.push(`/garage-manager/${vehicle.id}`);
  }

  async function printQrCode(event: GestureResponderEvent) {
    event.stopPropagation();

    if (printingQr) {
      return;
    }

    try {
      setPrintingQr(true);
      const result = await printGarageVehicleQrPlacard(vehicle);

      if (result === 'copied') {
        showSuccess(
          'Garage link copied. Open Frienzzi on web or PWA to print the QR placard.',
          'Print from web',
        );
      }
    } catch (error) {
      showError(error, 'Unable to prepare that Garage QR code.');
    } finally {
      setPrintingQr(false);
    }
  }

  function openShareMenu(event: GestureResponderEvent) {
    event.stopPropagation();

    if (!shareURL) {
      showError(
        new Error('Missing Garage URL.'),
        'Unable to share this Garage vehicle.',
      );
      return;
    }

    setShareMenuVisible(true);
  }

  return (
    <PressableScale
      accessibilityLabel={`Open ${garageVehicleTitle(vehicle)}`}
      accessibilityRole="button"
      onPress={openVehicle}
      scaleTo={0.982}
      style={({ pressed }) => [
        styles.card,
        pressed ? styles.cardPressed : null,
      ]}
    >
      <View style={styles.photoFrame}>
        {imageURL ? (
          <Image
            accessibilityLabel={`${garageVehicleTitle(vehicle)} cover photo`}
            cachePolicy="memory-disk"
            contentFit="cover"
            source={{ uri: imageURL }}
            style={styles.photo}
          />
        ) : (
          <View style={styles.photoPlaceholder}>
            <CarFront color={colors.secondary} size={34} strokeWidth={2.1} />
          </View>
        )}
      </View>
      <View style={styles.content}>
        <View style={styles.titleRow}>
          <View style={styles.titleText}>
            <Text numberOfLines={1} style={styles.title}>
              {garageVehicleTitle(vehicle)}
            </Text>
            {vehicle.trim ? (
              <Text numberOfLines={1} style={styles.subtitle}>
                {vehicle.trim}
              </Text>
            ) : null}
          </View>
          <View style={styles.statusPill}>
            <Text style={styles.statusText}>
              {ownershipLabel(vehicle.ownershipStatus)}
            </Text>
          </View>
          <Pressable
            accessibilityLabel="Share Garage vehicle"
            accessibilityRole="button"
            disabled={!shareURL}
            onPress={openShareMenu}
            style={({ pressed }) => [
              styles.shareIconButton,
              pressed && shareURL ? styles.shareIconButtonPressed : null,
              !shareURL ? styles.shareIconButtonDisabled : null,
            ]}
          >
            <Share2 color={colors.secondary} size={18} strokeWidth={2.4} />
          </Pressable>
        </View>
        <View style={styles.metaRow}>
          <Text style={styles.metaText}>{vehicle.photoCount} photos</Text>
          <Text style={styles.metaDot}>·</Text>
          <Text style={styles.metaText}>{vehicle.commentCount} comments</Text>
          <Text style={styles.metaDot}>·</Text>
          <Text style={styles.metaText}>{vehicle.reactionCount} reactions</Text>
        </View>
        {canEdit ? (
          <View style={styles.cardActions}>
            <Button
              title="Edit vehicle"
              variant="secondary"
              onPress={editVehicle}
              style={styles.editButton}
            />
            <Button
              title="Print QR code"
              variant="secondary"
              loading={printingQr}
              onPress={printQrCode}
              style={styles.printButton}
            />
          </View>
        ) : null}
      </View>
      <GarageVehicleShareModal
        canShareOnFrienzzi={canShareOnFrienzzi}
        source="garage_card"
        vehicle={vehicle}
        visible={shareMenuVisible}
        onClose={() => setShareMenuVisible(false)}
      />
    </PressableScale>
  );
}

export function GarageSection({
  canEdit,
  emptyText,
  loading,
  onAdd,
  title = 'Garage',
  vehicles,
}: {
  canEdit?: boolean;
  emptyText: string;
  loading?: boolean;
  onAdd?: () => void;
  title?: string;
  vehicles: GarageVehicle[];
}) {
  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>{title}</Text>
        {onAdd ? (
          <Button title="Add vehicle" variant="secondary" onPress={onAdd} />
        ) : null}
      </View>
      {loading ? (
        <Text style={styles.emptyText}>Loading Garage...</Text>
      ) : vehicles.length > 0 ? (
        <View style={styles.cardList}>
          {vehicles.map((vehicle) => (
            <GarageCard key={vehicle.id} canEdit={canEdit} vehicle={vehicle} />
          ))}
        </View>
      ) : (
        <View style={styles.emptyState}>
          <Text style={styles.emptyTitle}>No vehicles yet</Text>
          <Text style={styles.emptyText}>{emptyText}</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
  },
  cardPressed: {
    backgroundColor: colors.surfacePressed,
  },
  photoFrame: {
    aspectRatio: 16 / 9,
    backgroundColor: colors.surfaceTint,
  },
  photo: {
    height: '100%',
    width: '100%',
  },
  photoPlaceholder: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  content: {
    gap: spacing.sm,
    padding: spacing.md,
  },
  titleRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  titleText: {
    flex: 1,
    gap: 2,
  },
  title: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
  },
  subtitle: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '700',
  },
  statusPill: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
  },
  statusText: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
  },
  shareIconButton: {
    alignItems: 'center',
    borderColor: colors.border,
    borderRadius: 18,
    borderWidth: 1,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  shareIconButtonDisabled: {
    opacity: 0.45,
  },
  shareIconButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  metaRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  metaText: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '700',
  },
  metaDot: {
    color: colors.mutedText,
    fontSize: 13,
  },
  editButton: {
    minWidth: 132,
  },
  cardActions: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    justifyContent: 'space-between',
    marginTop: spacing.xs,
  },
  printButton: {
    minWidth: 152,
  },
  section: {
    gap: spacing.md,
    marginBottom: spacing.xl,
    marginTop: spacing.lg,
  },
  sectionHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  sectionTitle: {
    color: colors.text,
    fontSize: 22,
    fontWeight: '900',
  },
  cardList: {
    gap: spacing.md,
  },
  emptyState: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
  },
  emptyText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
});
